import React, {useState, useContext, useCallback, useEffect} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Typography,
    TextField,
    Grid,
    IconButton,
    makeStyles
} from '@material-ui/core';
import { useTranslation } from "react-i18next";
import SecureFetchContext from 'context/SecureFetchContext';
import getApiRoute from 'routes/apiRoutes';
import Alert from '@material-ui/lab/Alert';
import AppContext from 'context/AppContext';
import InputAdornment from '@material-ui/core/InputAdornment';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import ClearIcon from '@material-ui/icons/Clear';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useStyles = makeStyles(() => ({
    root: {
    }
}));

const CodPriceForm = ({ className, pricelist, handleDialogOpenClose, loadPrices, ...rest }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const secureFetch = useContext(SecureFetchContext);
    const [values, setValues] = useState({});
    const [states, setStates] = useState({});
    const [currency, setCurrency] = useState(0);
    const [rowCount, setRowCount] = useState(1);
    const { appValues } = useContext(AppContext);
    const [alertMessage, setAlertMessage] = useState('');
    const [carriers, setCarriers] = useState([]);

    const handleChange = (event, index) => {
        let tmpValues = values;
        tmpValues[index] = { ...tmpValues[index], [event.target.name]: event.target.value };
        setValues({ ...tmpValues });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if ( !states.states_to || states.states_to.length === 0 ) {
            setAlertMessage(t('messages.states_not_blank'));
            return;
        }
        for (const key in values) {
            values[key].state_to = states.states_to.map(state => state.id);
            values[key].price_list = pricelist.id;
            values[key].currency = currency.id;
        }
        secureFetch(getApiRoute('admin_codprice'), { prices: values }, 'POST').then(json => {
            setAlertMessage('');
            handleDialogOpenClose();
            loadPrices();
        });
    };

    useEffect(() => {
        if (pricelist.id && pricelist.shipper.shortcut === 'Packeta') {
            console.log('loading carriers')
                secureFetch(getApiRoute('admin_carrier_available')).then(json => {
                    setCarriers(json);
                });
        }
    }, [pricelist]);

    const addRow = () => {
        setRowCount( rowCount + 1 );
    };

    const removeRow = (index) => {
        let tmpValues = values;
        while(index + 1 < rowCount){
            tmpValues[index] = tmpValues[index + 1];
            index++;
        }
        delete tmpValues[rowCount - 1];
        setValues({ ...tmpValues });
        setRowCount( rowCount - 1 );
    };

    const rows = [];
    for (let i = 0; i < rowCount; i++) {
        rows.push(
            <React.Fragment key={i} >
                <Grid item xs={2}>
                    <TextField
                        fullWidth
                        label={t('label.amount_from')}
                        name="amount_from"
                        onChange={ (event) => { handleChange(event, i) } }
                        value={values[i] && values[i].amount_from ? values[i].amount_from : ''}
                        type="number"
                        inputProps={{ step: "0.001" }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{currency ? currency.symbol : '' }</InputAdornment>,
                        }}
                        required
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        fullWidth
                        label={t('label.amount_to')}
                        name="amount_to"
                        onChange={ (event) => { handleChange(event, i) } }
                        value={values[i] && values[i].amount_to ? values[i].amount_to : ''}
                        type="number"
                        inputProps={{ step: "0.001" }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{currency ? currency.symbol : '' }</InputAdornment>,
                        }}
                        required
                    />
                </Grid>
                <Grid item xs={1}>
                    <TextField
                        fullWidth
                        label={t('label.percentage')}
                        name="percentage"
                        onChange={ (event) => { handleChange(event, i) } }
                        value={values[i] && values[i].percentage ? values[i].percentage : ''}
                        type="number"
                        inputProps={{ step: "0.001" }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">%</InputAdornment>,
                        }}
                        required
                    />
                </Grid>
                <Grid item xs={2}>
                    <FormControl className={classes.formControl} fullWidth>
                        <InputLabel >{t('label.payment_type')}</InputLabel>
                        <Select
                            value={values[i] && values[i].payment_type != null ? values[i].payment_type : ''}
                            name="payment_type"
                            onChange={ (event) => { handleChange(event, i) } }
                            required
                        >
                            {appValues.paymentTypes.map((option) => (
                                <MenuItem 
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={2}>
                    <FormControl className={classes.formControl} fullWidth>
                        <InputLabel >{t('label.delivery_type')}</InputLabel>
                        <Select
                            value={values[i] && values[i].delivery_type != null ? values[i].delivery_type : ''}
                            name="delivery_type"
                            onChange={ (event) => { handleChange(event, i) } }
                            required
                        >
                            {appValues.deliveryTypes.map((option) => (
                                <MenuItem 
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={1}>
                    <TextField
                        fullWidth
                        label={t('label.price')}
                        name="price"
                        onChange={ (event) => { handleChange(event, i) } }
                        value={values[i] && values[i].price ? values[i].price : ''}
                        type="number"
                        inputProps={{ step: "0.001" }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        }}
                        required
                    />
                </Grid>
                <Grid item xs={2}>
                    { pricelist.shipper.shortcut == 'Packeta' && carriers.length !== 0 &&
                        <FormControl className={classes.formControl} fullWidth>
                            <InputLabel >{t('label.carrier_type')}</InputLabel>
                            <Select
                                value={values[i] && values[i].carrier_type != null ? values[i].carrier_type : ''}
                                name="carrier_type"
                                onChange={(event) => { handleChange(event, i)} }
                                required
                            >
                                {carriers.map((option) => (
                                    <MenuItem
                                        key={option.packeta_id}
                                        value={option.packeta_id}
                                    >
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    }
                </Grid>
                <Grid item xs={1}>
                    <IconButton onClick={ () => removeRow(i)} size="small" >
                        <ClearIcon />
                    </IconButton>
                </Grid>
            </React.Fragment>
        );
    }

    return (
        <form
            autoComplete="off"
            className={clsx(classes.root, className)}
            onSubmit={handleSubmit}
            {...rest}
        >
            <Typography variant="h3" gutterBottom >
                {t('title.create_price')}
            </Typography>
            <Grid container spacing={4} alignItems="center">
                <Grid item xs={6}>
                    <Autocomplete
                        options={appValues.currencies}
                        onChange={ (event, pCurrency) => setCurrency(pCurrency) }
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => <TextField {...params} label={t('label.currency')} required/>}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Autocomplete
                        multiple
                        options={appValues.states}
                        onChange={ (event, pStates) => setStates({ ...states, states_to: pStates }) }
                        disableCloseOnSelect
                        getOptionLabel={(option) => option.name}
                        renderOption={(option, { selected }) => (
                            <React.Fragment>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                            />
                            {option.name}
                            </React.Fragment>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} label={t('label.states_to')} placeholder={t('label.state')} />
                        )}
                        />
                </Grid>
                {rows}
            </Grid>
            <Box display="flex" py={2}>
                <Button color="secondary" variant="text" onClick={addRow} >
                    {t('button.add_price')}
                </Button>
            </Box>
            { 
                alertMessage === '' ? null : 
                <Box>
                    <Alert variant="filled" severity="error">
                        {alertMessage}
                    </Alert>
                </Box>
            }
            <Box display="flex" justifyContent="flex-end" py={2}>
                <Button color="secondary" variant="text" onClick={handleDialogOpenClose} >
                    {t('button.close')}
                </Button>
                <Button color="primary" variant="contained" type="submit">
                    {t('button.create')}
                </Button>
            </Box>
        </form>
    );
};

CodPriceForm.propTypes = {
    className: PropTypes.string
};

export default CodPriceForm;
